import { api } from "@/lib/http.ts";
import type { InstantBookDayResponse } from "@/types/instantBook.ts";
import { queryOptions } from "@tanstack/react-query";

/**
 * One day of the Instant Book picker grid.
 *
 * Keyed by day so paging between days is instant on the way back, with a short
 * `staleTime`: the user is choosing a time against other people's calendars, so
 * a few-minute-old view is fine but a session-old one is misleading.
 */
export const instantBookQueries = {
  all: () => ["instantBookDay"] as const,
  day: (
    threadId: number,
    messageId: number,
    artifactId: string,
    /** `YYYY-MM-DD`; omit to let the server open on the placed slot's day. */
    date: string | undefined,
    /**
     * Who the grid is drawing, from the artifact view. Not sent — the endpoint
     * reads the participants off the artifact — but part of the key, so adding
     * someone to the card fetches their column immediately rather than leaving
     * the cached day standing until something else invalidated it.
     */
    participantsKey: string,
  ) =>
    queryOptions({
      queryKey: [
        ...instantBookQueries.all(),
        threadId,
        messageId,
        artifactId,
        date,
        participantsKey,
      ] as const,
      queryFn: async ({ signal }) => {
        const query = date ? `?date=${date}` : "";
        const response = await api.get<InstantBookDayResponse>(
          `/api/a/threads/${threadId}/messages/${messageId}/artifacts/${artifactId}/instantBookDay${query}`,
          { signal },
        );
        return response.data;
      },
      staleTime: 60_000,
      // Paging days changes the key; without this the whole grid is replaced by
      // a skeleton on every prev/next click. Mirrors `useCalendarEvents`.
      placeholderData: (previous) => previous,
    }),
};
